<template>
  <div v-if="pending" class="el-button is-disabled mb-2 me-2" :class="buttonClass">
    <i class="loader material-icons md-18">sync</i>
  </div>
  <div v-else-if="writeOrRead" :class="buttonContainerClass" :key="`${url}${componentKey}`">
    <a @click.prevent="onClick" :class="anchorClass" :href="url" :target="target">
      <slot>
        <i :class="icon" class="pr5" v-if="icon"></i>
        {{ buttonName }}
      </slot>
    </a>
  </div>
</template>

<script>
/**
 * @param CommonAnchorButton component
 *
 * @description A listák gombjainak dinamikus megjelenítésére szolgáló component
 */
import Loader from '../../Loader'
import { mapActions } from 'vuex'
import EventBus from '../../../utils/event-bus'
import confirmation from '../../../utils/mixin-confirmation'
import { get, toArray, isEmpty, isUndefined } from 'lodash'
import ExpressionResolver from '../../../store/modules/form/helpers/expression-resolver-store'
import axios from '../../../axiosClient'
import notify from '../../../utils/mixin-notify'

export default {
  name: 'CommonAnchorButton',
  props: [
    'buttonStyle',
    'layoutDirection',
    'buttonListType',
    'isInExpanded',
    'currentViewName',
    'isDetailViewVisible',
    'icon',
    'name',
    'url',
    'confirm',
    'listCollapsedButton',
    'popup',
    'popupTitle',
    'formType',
    'rowOptions',
    'target',
    'createNewElement',
    'permission',
    'type',
    'groupReadOnly',
    'readOnly',
    'emitEvent',
  ],
  mixins: [confirmation, notify],
  components: {
    Loader,
  },
  async created() {
    if (!this.permission) {
      const response = await this.getPermissions({ url: this.url })
      this.perm = get(response, 'body')
    } else {
      this.perm = this.permission
    }
    this.pending = false
    this.confirmMessage = await this.getConfirmMessage()
  },
  mounted() {
    EventBus.$on('refresh-panel', this.refreshPanel)
  },
  beforeDestroy() {
    EventBus.$off('refresh-panel', this.refreshPanel)
  },
  methods: {
    ...mapActions('popup', ['setOpened', 'setUrl', 'setTitle', 'setType', 'setCreateNewElement']),
    async onClick() {
      if (this.isPopup) {
        this.setUrl({ url: this.url, event: this.emitEvent })
        this.setType(this.formType)
        this.setCreateNewElement(this.createNewElement)
        if (this.popupTitle) this.setTitle(this.popupTitle)
        if (this.createNewElement) {
          this.$emit('click-create-new-element')
        }
        this.setOpened()
        EventBus.$emit('refresh-form-expression', {})
      } else {
        const confirmed = await this.handleConfirmation()
        if (confirmed) await this.handleAction()
      }
    },
    async handleConfirmation() {
      let confirmed = true
      if (this.confirmMessage) {
        confirmed = await this._confirm(this.confirmMessage)
      }
      return confirmed
    },
    async getPermissions(parameters) {
      this.pending = true
      if (get(parameters, 'url')) {
        parameters.url = parameters.url.replace(this.API_BASE_PATH, '')
      }
      return this.$client.execute({
        operationId: 'Authorities_getPermissionByUrl',
        parameters,
      })
    },
    param(param) {
      const parameters = {}
      const hiddenSearchData = get(param, 'view.properties.hiddenSearchData')

      if (hiddenSearchData) {
        hiddenSearchData.where.forEach((e) => {
          parameters['pk'] = e
        })
      }

      const mongoId = get(param, 'view.properties.mongoId')
      if (mongoId) {
        parameters['mongoId'] = mongoId
      }

      return parameters
    },
    async getConfirmMessage() {
      if (this.type === 'nodeProcess' && isUndefined(this.confirm)) {
        const match = this.url.match(/id=(.*)&modelId=/) // @regex process url-ekből a processz mongo-s id-jára match-el

        if (isEmpty(match)) return this.confirm
        const id = match[1]

        const response = await this.$client.execute({
          operationId: 'ProcessBuilder_findOne',
          parameters: {
            filter: JSON.stringify({
              where: { id },
            }),
          },
        })

        const process = response.body
        const confirmExpression = process['3--confirm']

        if (process && confirmExpression) {
          const active = await ExpressionResolver.resolve({
            expression: confirmExpression,
            namespace: this.$storeNamespace,
          })
          if (active) {
            return process['3--confirmMessage']
          }
        }
      }

      return this.confirm
    },
    translateParams(params) {
      Object.keys(params).forEach((key) => {
        params[key] = this.$i18n.t(params[key])
      })
      return params
    },
    handleAction() {
      if (this.isProcess) {
        this.handleProcessAction()
      } else if (!this.target || this.target === '_self') {
        this.$router.push(this.url)
      } else {
        window.open(this.url, this.target)
      }
    },
    emitEvents() {
      EventBus.$emit('refresh-layout')
      EventBus.$emit('show-notifications')
    },
    async handleProcessAction() {
      this.pending = true

      const response = await axios()
        .get(this.url, { params: { redirect: false } })
        .catch((error) => ({ error }))
        .finally(() => (this.pending = false))

      this.pending = false
      if (response.error) {
        return this.showNotification({
          title: this.$i18n.t('error_occurred'),
          text: this.$i18n.t('error_occurred: ${buttonName}'),
          params: {
            buttonName: this.buttonName,
          },
          type: 'error',
        })
      }

      const redirectUrl = get(response, 'data.location')
      if (redirectUrl) window.open(redirectUrl, this.target)

      this.emitEvents()
    },
    async refreshPanel() {
      const response = await this.getPermissions({ url: this.url })
      this.perm = get(response, 'body')
      this.pending = false
      this.componentKey++
    },
  },
  data() {
    return {
      pending: false,
      perm: null,
      componentKey: 0,
      parameters: {},
      confirmMessage: '',
      PROCESS_TYPE: 'nodeProcess',
      API_BASE_PATH: get(window, 'CORE.apiBasePath'),
    }
  },
  computed: {
    buttonContainerClass() {
      if (this.layoutDirection === 'horizontal-button-layout') return 'me-1 mb-1'
      if ((this.isInExpanded && !this.isDetailViewVisible) || this.currentViewName === 'detailView')
        return 'col-md-6 split'
    },
    anchorClass() {
      if (this.buttonStyle) return this.buttonStyle
      let classString = 'el-button btn mb-2 text-truncate'
      classString += this.name === 'Delete' ? ' el-button--danger' : ' el-button--default is-plain'
      if (this.isInExpanded) {
        classString += ' el-button--small'
      }
      return classString
    },
    buttonClass() {
      let classString = ''
      if (this.layoutDirection === 'vertical-button-layout') classString += ' p-1'
      if (this.isInExpanded) {
        classString += ' el-button--small p-1'
      } else {
        classString += ' el-button--small'
      }
      return classString
    },
    buttonName() {
      return typeof this.name === 'string'
        ? this.$i18n.t(this.name)
        : this.$i18n.interpolate(
            this.$i18n.t(this.name.text, { category: 'CommonAnchorButton' }),
            this.translateParams(this.name.params)
          )
    },
    isPopup() {
      return this.$attrs.popup || this.popup
    },
    writeOrRead() {
      if (!this.groupReadOnly && !this.readOnly && this.perm) {
        const permissionValues = Object.values(this.perm)
        const permissions = get(permissionValues, '0')
        return toArray(permissions).every((el) => el == 1)
      } else {
        return false
      }
    },
    isProcess() {
      return this.type === this.PROCESS_TYPE
    },
  },
}
</script>

<style lang="scss" scoped>
.split {
  padding: 0 1px;
}
.el-button {
  width: 100%;
  &.is-disabled {
    border-style: dashed;
  }
}
@keyframes rotating {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.loader {
  transform: rotate(0);
  animation-name: rotating;
  animation-duration: 0.75s;
  animation-iteration-count: infinite;
}
</style>
